import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { FiX } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { fundraiseService } from '../../services/fundraiseService';
import { clearPendingClaim, readPendingClaim } from '../../utils/pendingCollabClaim';

const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;

const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'https://api.sendupdates.ai';

interface PreviewState {
  pipelineName: string;
  ownerLabel: string | null;
  permissionLevel: 'view' | 'collaborate';
}

// Watches for a localStorage-pending fundraise collaborator token. As soon as
// the auth context has a session AND the token is present, this fetches the
// pipeline preview from the public endpoint, shows a small confirm modal, and
// only POSTs the claim once the user clicks Accept. Mounted at the app root
// (next to AppRoutes) so it can fire on any post-auth page, including the
// dashboard the signup flow normally lands on.
const PendingCollabClaimModal: React.FC = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [token, setToken] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Detect the pending token only after auth resolves to "logged in".
  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    const pending = readPendingClaim();
    if (!pending) return;
    setToken(pending);
  }, [isAuthenticated, authLoading]);

  // Fetch the public pipeline preview (pipeline name + owner name) so the
  // modal can show *what* is being accepted before the user clicks Accept.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const fetchPreview = async () => {
      try {
        const url = `${API_BASE_URL}/api/public/pipeline/${encodeURIComponent(token)}`;
        const res = await axios.get(url);
        if (cancelled) return;
        const data = res.data?.data;
        if (!data?.pipeline || !data?.shareLink) {
          throw new Error('Invalid share link payload');
        }
        if (data.shareLink.permission_level !== 'collaborate') {
          // View-only link — nothing to claim. Quietly clear.
          clearPendingClaim();
          setToken(null);
          return;
        }
        setPreview({
          pipelineName: data.pipeline.name,
          ownerLabel:
            (data.pipeline.owner_name as string | null) ||
            (data.pipeline.owner_email as string | null) ||
            null,
          permissionLevel: 'collaborate',
        });
      } catch (e: any) {
        if (cancelled) return;
        // Token invalid / expired / inactive — clear it so we don't keep
        // re-showing the modal on every page load.
        clearPendingClaim();
        setToken(null);
      }
    };
    fetchPreview();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleDecline = useCallback(() => {
    clearPendingClaim();
    setToken(null);
    setPreview(null);
  }, []);

  const handleAccept = useCallback(async () => {
    if (!token) return;
    setSubmitting(true);
    setError(null);
    try {
      await fundraiseService.claimCollaboratorInvite(token);
      clearPendingClaim();
      setToken(null);
      setPreview(null);
      navigate('/fundraise');
    } catch (e: any) {
      const message =
        e?.response?.data?.error || e?.message || 'Could not accept invite';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }, [token, navigate]);

  if (!token || !preview) return null;

  const modal = (
    <div className="su-fundraise-modal-overlay">
      <div
        className="su-fundraise-modal"
        style={{ maxWidth: 480 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pending-claim-title"
      >
        <div className="su-fundraise-modal-header">
          <div>
            <h2 id="pending-claim-title" className="su-fundraise-modal-title">
              Accept collaborator invite
            </h2>
            <p className="su-fundraise-modal-subtitle">
              {preview.ownerLabel
                ? `${preview.ownerLabel} invited you to collaborate.`
                : 'You were invited to collaborate.'}
            </p>
          </div>
          <button
            type="button"
            className="su-fundraise-modal-close"
            onClick={handleDecline}
            aria-label="Dismiss invite"
          >
            <CloseIcon width={20} height={20} />
          </button>
        </div>

        <div className="su-fundraise-modal-body">
          {error && <div className="su-fundraise-error-message">{error}</div>}
          <p style={{ color: 'rgba(255,255,255,0.85)' }}>
            You&apos;ll be added as a collaborator on{' '}
            <strong>{preview.pipelineName}</strong>. You&apos;ll be able to
            edit and move funds, but won&apos;t be able to delete the pipeline
            or its funds. The owner is notified when you accept.
          </p>
        </div>

        <div className="su-fundraise-modal-footer">
          <button
            type="button"
            className="su-btn-secondary"
            onClick={handleDecline}
            disabled={submitting}
          >
            Not now
          </button>
          <button
            type="button"
            className="su-btn-primary"
            onClick={handleAccept}
            disabled={submitting}
          >
            {submitting ? 'Accepting…' : 'Accept invite'}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default PendingCollabClaimModal;
